import { TMatchingResultsItem, TMatchingResultsMetric, TMatchingResultsSortBy } from "@/features/dashboard/types/matching-results";

// Per-metric configuration: drives modal title, date column label, and which date field to render.
export const METRIC_CONFIG: Record<
  TMatchingResultsMetric,
  { label: string; dateLabel: string; dateField: keyof TMatchingResultsItem }
> = {
  proposing: { label: "推薦数", dateLabel: "推薦日", dateField: "proposalDate" },
  interview: { label: "面接数", dateLabel: "面接日", dateField: "interviewDate" },
  offer: { label: "内定数", dateLabel: "内定日", dateField: "companyOfferDate" },
  accept: { label: "内定受諾数", dateLabel: "内定受諾日", dateField: "offerAcceptDate" },
};

// Column identifier union - matches BE sortBy whitelist plus the non-sortable status column.
export type TMatchingResultsColumnId =
  | "status"
  | "talentName"
  | "caseName"
  | "jobName"
  | "companySpecificIndustry"
  | "companyName"
  | "metricDate"
  | "caseOwner"
  | "jobRecruiter"
  | "talentPersonInCharge";

export type TMatchingResultsColumnDef = {
  id: TMatchingResultsColumnId;
  label: string; // `metricDate` label derived from METRIC_CONFIG at render time
  sortable: boolean;
  sortBy?: TMatchingResultsSortBy;
  width: number;
  filter?: { kind: "enum" | "text" | "date"; options?: unknown };
};

// Full column list in display order. `metricDate` gets its label from METRIC_CONFIG[metric].dateLabel.
// Widths per design spec (SVG).
export const MATCHING_RESULTS_COLUMNS: TMatchingResultsColumnDef[] = [
  { id: "status", label: "ステータス", sortable: true, sortBy: "status", width: 134 },
  { id: "talentName", label: "人材名", sortable: true, sortBy: "talentName", width: 140 },
  { id: "caseName", label: "案件名", sortable: true, sortBy: "caseName", width: 140 },
  { id: "jobName", label: "求人名", sortable: true, sortBy: "jobName", width: 140 },
  { id: "companySpecificIndustry", label: "分野", sortable: true, sortBy: "companySpecificIndustry", width: 130 },
  { id: "companyName", label: "企業名", sortable: true, sortBy: "companyName", width: 140 },
  { id: "metricDate", label: "", sortable: true, sortBy: "metricDate", width: 106 },
  { id: "caseOwner", label: "案件担当者", sortable: true, sortBy: "caseOwner", width: 134 },
  { id: "jobRecruiter", label: "求人担当", sortable: true, sortBy: "jobRecruiter", width: 134 },
  { id: "talentPersonInCharge", label: "人材担当", sortable: true, sortBy: "talentPersonInCharge", width: 134 },
];

// All column ids - used to validate stored visibility (a hidden-by-default column the user turned on must survive reload).
export const ALL_COLUMN_IDS: TMatchingResultsColumnId[] = MATCHING_RESULTS_COLUMNS.map((c) => c.id);

// Columns hidden until the user turns them on.
const HIDDEN_BY_DEFAULT_COLUMN_IDS: TMatchingResultsColumnId[] = ["jobRecruiter", "talentPersonInCharge"];

// Default column visibility (all visible except HIDDEN_BY_DEFAULT_COLUMN_IDS).
export const DEFAULT_VISIBLE_COLUMN_IDS: TMatchingResultsColumnId[] = ALL_COLUMN_IDS.filter((id) => !HIDDEN_BY_DEFAULT_COLUMN_IDS.includes(id));

// Resolve the display label for a column, accounting for the active metric's date label.
export const resolveColumnLabel = (col: TMatchingResultsColumnDef, metric: TMatchingResultsMetric): string => {
  if (col.id === "metricDate") return METRIC_CONFIG[metric].dateLabel;
  return col.label;
};
